import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { blogPosts } from './blog-posts';
import { Calendar, Clock, ArrowRight, Search } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export default function Blog() {
  const [searchTerm, setSearchTerm] = useState('');
  const { openAuthModal } = useAuth();

  const filteredPosts = blogPosts.filter(post =>
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>Roofing Blog - Measurement Guides & Software Reviews | SquareCount</title>
        <meta name="description" content="Expert roofing guides: EagleView alternatives, roof measurement best practices, pitch calculators, and contractor software reviews. Save money on every estimate." />
        <meta property="og:title" content="Roofing Industry Blog | SquareCount.io" />
        <meta property="og:description" content="Expert roofing guides: EagleView alternatives, roof measurement best practices, and contractor software reviews." />
        <link rel="canonical" href="https://squarecount.io/blog" />
      </Helmet>
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl mb-6">
            Roofing Measurement Guides & Software Reviews
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl">
            Expert guides, comparisons, and tips for roofing contractors. Learn how to save money, improve accuracy, and grow your business.
          </p>
        </div>
      </section>

      {/* Search */}
      <section className="max-w-7xl mx-auto px-6 -mt-8">
        <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-4 flex items-center gap-3">
          <Search className="w-5 h-5 text-slate-500" />
          <input
            type="text"
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 outline-none bg-transparent text-white placeholder:text-slate-500"
          />
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.map((post) => (
            <Link key={post.id} to={`/blog/${post.slug}`}>
              <div className="h-full bg-slate-900/50 border border-slate-800 hover:border-orange-500/30 rounded-lg transition-all cursor-pointer group overflow-hidden">
                <div className="aspect-video bg-gradient-to-br from-orange-500 to-orange-600 relative overflow-hidden">
                  {post.image ? (
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <span className="text-white font-heading font-bold text-4xl opacity-50">
                        {post.title.split(' ').slice(0, 2).map(w => w[0]).join('')}
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {post.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {post.readTime}
                    </span>
                  </div>
                  <h2 className="font-heading font-bold text-xl text-white group-hover:text-orange-400 transition-colors mb-3">
                    {post.title}
                  </h2>
                  <p className="text-slate-400 mb-4">{post.excerpt}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.slice(0, 3).map((tag, i) => (
                      <span key={i} className="text-xs bg-slate-800 text-slate-400 px-2 py-1 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center text-orange-500 font-semibold group-hover:gap-3 transition-all">
                    Read More
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-500 text-lg">No articles found. Try a different search term.</p>
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="bg-orange-500 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-4">
            Ready to Transform Your Roofing Business?
          </h2>
          <p className="text-xl mb-8 text-orange-100">
            Get instant roof measurements starting at $0/month. No credit card required.
          </p>
          <Button
            size="lg"
            className="bg-white text-orange-500 hover:bg-slate-100 font-heading font-bold text-lg px-8 py-6"
            onClick={() => openAuthModal('register')}
          >
            Start Your 7-Day Contractor Trial
          </Button>
        </div>
      </section>
      <Footer />
    </div>
  );
}
